import React, { FC, useCallback } from "react";
import {
    ActionBarButton as ActionBarButtonConfig,
    ActionBarFormButton,
    ActionBarScheduleButton,
    isActionBarChatButton,
    isActionBarFormButton,
    isActionBarScheduleButton,
    isActionBarPhoneButton,
    isActionBarUrlButton,
    isActionBarEmailButton,
    isActionBarHelpButton,
    isActionBarPaymentButton,
} from "@xapp/stentor-chat-widget";
import ActionBarIcon from "./ActionBarIcon";

/**
 * Allowed URL protocols for security. Prevents javascript: and other potentially dangerous protocols.
 */
const ALLOWED_PROTOCOLS = ["http:", "https:", "tel:", "mailto:"];

/**
 * Validates a URL to ensure it uses an allowed protocol.
 * Returns true if the URL is safe to navigate to.
 */
function isValidUrl(url: string): boolean {
    try {
        const parsed = new URL(url, window.location.href);
        return ALLOWED_PROTOCOLS.includes(parsed.protocol);
    } catch {
        // Invalid URL
        return false;
    }
}

/**
 * Safely navigates to a URL after validation.
 * Returns false if the URL was invalid and navigation was blocked.
 */
function safeNavigate(url: string, target: "sameWindow" | "newTab" | "newWindow", windowOptions?: { width?: number; height?: number }): boolean {
    if (!isValidUrl(url)) {
        console.warn("[ActionBar] Blocked navigation to unsafe URL:", url);
        return false;
    }

    if (target === "sameWindow") {
        window.location.href = url;
    } else if (target === "newWindow" && windowOptions) {
        const features = `width=${windowOptions.width || 600},height=${windowOptions.height || 400}`;
        window.open(url, "_blank", features);
    } else {
        // newTab (default)
        window.open(url, "_blank", "noopener,noreferrer");
    }
    return true;
}

export interface ActionBarButtonProps {
    readonly button: ActionBarButtonConfig;
    /**
     * Whether the chat widget is currently active/open
     */
    readonly chatActive: boolean;
    /**
     * Called when chat button is clicked to open chat
     */
    readonly onChatClick: () => void;
    /**
     * Called when chat button is clicked to minimize/close chat
     */
    readonly onChatMinimize: () => void;
    /**
     * Called when form or schedule button is clicked.
     * Both button types open the form widget modal.
     */
    readonly onFormClick: (button: ActionBarFormButton | ActionBarScheduleButton) => void;
    readonly isMobile: boolean;
}

const defaultLabels: Record<string, string> = {
    chat: "Chat",
    form: "Contact",
    schedule: "Book",
    phone: "Call",
    url: "Link",
    email: "Email",
    help: "Help",
    payment: "Pay",
};

const ActionBarButton: FC<ActionBarButtonProps> = ({
    button,
    chatActive,
    onChatClick,
    onChatMinimize,
    onFormClick,
    isMobile,
}) => {
    const handleClick = useCallback(() => {
        if (isActionBarChatButton(button)) {
            // Toggle chat: if active, minimize; if not active, open
            if (chatActive) {
                onChatMinimize();
            } else {
                onChatClick();
            }
        } else if (isActionBarFormButton(button) || isActionBarScheduleButton(button)) {
            // Both form and schedule buttons open the form widget modal
            onFormClick(button);
        } else if (isActionBarPhoneButton(button)) {
            // Phone: Use tel: link
            const phoneNumber = button.phoneNumber.replace(/\s/g, "");
            window.location.href = `tel:${phoneNumber}`;
        } else if (isActionBarUrlButton(button)) {
            // URL: Open according to target (with validation)
            const { url, target = "newTab", windowOptions } = button;
            safeNavigate(url, target, windowOptions);
        } else if (isActionBarEmailButton(button)) {
            // Email: Build mailto: link with optional subject and body
            const { emailAddress, subject, body } = button;
            let mailto = `mailto:${emailAddress}`;
            const params: string[] = [];
            if (subject) {
                params.push(`subject=${encodeURIComponent(subject)}`);
            }
            if (body) {
                params.push(`body=${encodeURIComponent(body)}`);
            }
            if (params.length > 0) {
                mailto += `?${params.join("&")}`;
            }
            window.location.href = mailto;
        } else if (isActionBarHelpButton(button)) {
            // Help: Open URL according to target (with validation)
            const { url, target = "newTab" } = button;
            safeNavigate(url, target);
        } else if (isActionBarPaymentButton(button)) {
            // Payment: Open URL according to target (with validation)
            const { url, target = "newTab" } = button;
            safeNavigate(url, target);
        }
    }, [button, chatActive, onChatClick, onChatMinimize, onFormClick]);

    // Handle visibility
    const visibility = button.visibility || "all";
    if (visibility === "mobile-only" && !isMobile) {
        return null;
    }
    if (visibility === "desktop-only" && isMobile) {
        return null;
    }

    const ariaLabel = button.label || defaultLabels[button.type] || button.type;
    const tabIndex = button.tabIndex ?? 0;

    // Chat button shows active state when chat is open
    const isActive = isActionBarChatButton(button) && chatActive;
    const buttonClassName = [
        "xapp-action-bar__button",
        isActive ? "xapp-action-bar__button--active" : "",
    ].filter(Boolean).join(" ");

    return (
        <button
            className={buttonClassName}
            onClick={handleClick}
            aria-label={ariaLabel}
            aria-pressed={isActive}
            tabIndex={tabIndex}
        >
            <ActionBarIcon
                type={button.type}
                icon={button.icon}
                iconUrl={button.iconUrl}
                className="xapp-action-bar__icon"
            />
            {button.label && (
                <span className="xapp-action-bar__label">{button.label}</span>
            )}
        </button>
    );
};

export default ActionBarButton;
